import React, { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import BrandLockup from "@/components/BrandLockup";
import SupportFooter from "@/components/SupportFooter";
import { useAuth } from "@/contexts/AuthContext";

type ThemeMode = "light" | "dark" | "auto";

function computeAutoIsDark(now: Date) {
  const h = now.getHours();
  return h >= 19 || h < 7;
}

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  const isDark = mode === "dark" || (mode === "auto" && computeAutoIsDark(new Date()));
  root.classList.toggle("dark", isDark);
}

function getSupportTopic(pathname: string): string {
  if (pathname === "/login") return "logging in";
  if (pathname === "/auth/callback") return "completing sign in";
  if (pathname === "/reset-password") return "resetting your password";
  if (pathname === "/owner/signup") return "creating your owner identity";
  if (pathname === "/owner/verify-complete") return "verifying your email";
  if (pathname === "/owner/create-company") return "deploying compliance";
  return "getting started";
}

export default function CenteredBrandLayout() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const {
    initializing,
    isAuthenticated,
    signOut,
    firstName,
    employeeSystemId,
  } = useAuth();

  const isLoginPage = pathname === "/login";

  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const saved = (localStorage.getItem("pcu_theme") as ThemeMode | null) ?? "auto";
    return saved === "light" || saved === "dark" || saved === "auto" ? saved : "auto";
  });

  useEffect(() => {
    localStorage.setItem("pcu_theme", themeMode);
    applyTheme(themeMode);

    if (themeMode !== "auto") return;

    const id = window.setInterval(() => applyTheme("auto"), 60_000);
    return () => window.clearInterval(id);
  }, [themeMode]);

  async function onLogout() {
    await signOut();
    nav("/login", { replace: true });
  }

  const themeLabel = useMemo(() => {
    if (themeMode === "light") return "Light";
    if (themeMode === "dark") return "Dark";
    return "Auto";
  }, [themeMode]);

  const supportTopic = useMemo(() => getSupportTopic(pathname), [pathname]);

  function cycleTheme() {
    setThemeMode((m) => (m === "light" ? "dark" : m === "dark" ? "auto" : "light"));
  }

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased dark:bg-slate-950 dark:text-slate-100">
      <div className="sticky top-0 z-50 border-b border-slate-200/60 bg-white/90 backdrop-blur dark:border-slate-800/70 dark:bg-slate-950/80">
        <div className="mx-auto w-full max-w-md px-2 py-2">
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={cycleTheme}
              className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-slate-700 bg-white hover:bg-slate-50 transition dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
              title="Theme: Light / Dark / Auto (night)"
            >
              Theme: {themeLabel}
            </button>

            {!isLoginPage && !initializing && !isAuthenticated && (
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-slate-700 bg-white hover:bg-slate-50 transition dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
              >
                Login
              </Link>
            )}

            {!isLoginPage && !initializing && isAuthenticated && (
              <div className="flex items-center gap-2">
                {!!firstName && (
                  <span className="max-w-[140px] truncate text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {firstName}
                  </span>
                )}

                {!!employeeSystemId && (
                  <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
                    ID {employeeSystemId}
                  </span>
                )}

                <button
                  type="button"
                  onClick={onLogout}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-slate-700 bg-white hover:bg-slate-50 transition dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-[calc(100vh-44px)] flex items-center justify-center px-2 py-6">
        <div className="w-full max-w-md flex flex-col items-center">
          <div className="mb-4">
            <BrandLockup size="lg" centered showTagline showSupportCopy={false} />
          </div>

          <div className="w-full">
            <Outlet />
          </div>

          <div className="mt-6">
            <SupportFooter topic={supportTopic} centered />
          </div>
        </div>
      </div>
    </div>
  );
}